import type { FeedVacancyItem } from '~/entities/feed'
import type { SearchResult } from '~/entities/search/search-state'
import { loadListing } from '~/composables/listing/use-listing-preview'

export const LANDING_CITY = { alias: 'novosibirsk', titleIn: 'в Новосибирске', titleOf: 'Новосибирска' }

const FEED_SIZE = 8

export type LandingShowcase = {
  total: number
  vacancies: SearchResult[]
  feed: FeedVacancyItem[]
  companies: { name: string, logo: string }[]
}

/**
 * Real published vacancies for the guest landing: search cards, a short video
 * feed (one vacancy per company) and the logos of companies that are hiring.
 * Failures degrade to empty lists so the landing still renders.
 */
export function useLandingShowcase() {
  return useAsyncData<LandingShowcase>('landing-showcase', async () => {
    // The landing renders for guests, and the API shows a guest only the first
    // GUEST_SEARCH_LIMIT (10) results: one page is all there is.
    const { data } = await mainFetch('vacancies', {
      query: { type: 'search', city: LANDING_CITY.alias, perPage: 10, page: 1 },
    })
    // Our own openings are not social proof for the hiring businesses we show.
    const vacancies: SearchResult[] = (data.value?.vacancies?.items ?? [])
      .filter((vacancy: SearchResult) => !/sobes/i.test(vacancy.company?.name ?? ''))

    const companies = new Map<string, string>()
    for (const vacancy of vacancies) {
      if (vacancy.company?.name && vacancy.company.logo && !companies.has(vacancy.company.name)) {
        companies.set(vacancy.company.name, vacancy.company.logo)
      }
    }

    const feedCandidates = [...new Map(vacancies.map(vacancy => [vacancy.company?.name ?? vacancy.id, vacancy])).values()]
      .slice(0, FEED_SIZE)
    const previews = await Promise.all(feedCandidates.map(vacancy =>
      loadListing('vacancy', vacancy.id).then(result => result.ok ? result.data.item : null, () => null),
    ))
    const feed = previews.filter((item): item is FeedVacancyItem => item?.type === 'vacancy' && item.videos.length > 0)

    return {
      total: data.value?.vacancies?.count ?? 0,
      vacancies,
      feed,
      companies: [...companies].map(([name, logo]) => ({ name, logo })),
    }
  }, {
    default: () => ({ total: 0, vacancies: [], feed: [], companies: [] }),
  })
}
